/**
 * Format-Helfer de-DE (UX_KONZEPT.md §4.10): Datum TT.MM.JJJJ, Zahlen mit
 * Komma/Tausenderpunkt, Einheiten mit schmalem Leerzeichen ("12,5 kWh").
 *
 * Alle Werte aus der API kommen als String (Decimal/Date-ISO, camelCase-
 * Pydantic-Aliase) — die Helfer parsen defensiv und liefern "—" bei
 * fehlenden/unlesbaren Werten statt "NaN"/"Invalid Date" anzuzeigen.
 */

const NBSP = " " // schmales geschütztes Leerzeichen

const zahlFormat = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 3 })
const datumFormat = new Intl.DateTimeFormat("de-DE", { year: "numeric", month: "2-digit", day: "2-digit" })

/** ISO-Datum ("2026-06-15") oder Date → "15.06.2026"; ungültig/leer → "—". */
export function formatDatum(value: string | Date | null | undefined): string {
  if (!value) return "—"
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return "—"
  return datumFormat.format(date)
}

/** Numerischer String/Zahl → de-DE-Zahl ("1.234,5"); ungültig/leer → "—". */
export function formatZahl(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—"
  const num = typeof value === "number" ? value : Number(value)
  if (Number.isNaN(num)) return "—"
  return zahlFormat.format(num)
}

/** Numerischer String/Zahl + Einheit → "1.234,5 kWh" (schmales Leerzeichen); ungültig → "—". */
export function formatMitEinheit(
  value: string | number | null | undefined,
  einheit: string | null | undefined,
): string {
  const zahl = formatZahl(value)
  if (zahl === "—") return "—"
  return einheit ? `${zahl}${NBSP}${einheit}` : zahl
}

/** Kurzform speziell für kWh-Werte (häufigster Fall in Energie). */
export function formatKwh(value: string | number | null | undefined): string {
  return formatMitEinheit(value, "kWh")
}

/** Euro-Betrag mit 2–6 Nachkommastellen (Preise sind hochpräzise, ARCHITEKTUR.md §3). */
export function formatEuro(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—"
  const num = typeof value === "number" ? value : Number(value)
  if (Number.isNaN(num)) return "—"
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }).format(num)
}
